import React from 'react';
import {
  LineChart,
  Line,
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import RevisionDetails from './RevisionDetails';
import { InSpecIcon, OutOfSpecIcon } from '../Common/Icons';
import {
  expandCamelCase,
  formatWithUnits,
  nearestUp,
  nearestDown,
} from '../../utils/formatter';
import { Form, Button, Row, Col } from 'react-bootstrap';

/* 
  2. Concept analysis page (ConceptAnalysisLogs)
  - Displays chart breaking down revision analysis across several analysis fields which can be toggled on/off
  - Changelog, notes, and analysis for the selected revision
  - Design summary for the concept as a whole
  - Option to open it in Engineering
*/

/* Custom dot for the graphs:
 * - Show green checkmark or red dot for whether the revision is in-spec
 * - Select the revision on click
 */
const CustomizedDot = (props) => {
  const { active, selectRevision, cx, cy, payload } = props;
  const inSpec = payload['Specifications'] === 'In Spec';

  if (active) {
    return (
      <svg
        x={cx - 10}
        y={cy - 10}
        width={20}
        height={20}
        viewBox="0 0 1024 1024"
        onClick={() => selectRevision(payload.id)}
        className="hoverable"
      >
        {inSpec ? <InSpecIcon /> : <OutOfSpecIcon />}
      </svg>
    );
  }

  return (
    <svg
      x={cx - 7}
      y={cy - 7}
      width={14}
      height={14}
      viewBox="0 0 1024 1024"
      onClick={() => selectRevision(payload.id)}
      className="hoverable"
    >
      {inSpec ? <InSpecIcon /> : <OutOfSpecIcon />}
    </svg>
  );
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const revisionNumber = payload[0].payload.number;
    const performanceField = payload[0].name;
    const performanceValue = payload[0].value;

    return (
      <div className="bg-white border border-rounded p-2 border-primary">
        <strong>Revision {revisionNumber}</strong>
        <br />
        {performanceField}: {performanceValue}
      </div>
    );
  }

  return <></>;
};

const LogsForConcept = ({
  concept, // the concept to analyze
  selectConcept, // function to select a different concept
  selectedRevision, // the currently-selected revision
  designFields, // design analysis fields
  selectRevision, // selecting a revision in the journal
  openRevision, // open revision in engineering
  saveNotes, // save notes for a revision
  width, // width of this component
}) => {
  const [yAxisField, setYAxisField] = React.useState(
    expandCamelCase(designFields[0])
  );

  /* Data format is { name, ...design fields... diagnostics... } */
  const analysisFields = [
    ...concept.revisions[concept.revisions.length - 1].diagnostics
      .filter((d) => typeof d.value === 'number')
      .map((d) => ({
        label: d.label,
        unit: d.units,
      })),
  ];

  const data = concept.revisions
    .filter((revision) => revision.diagnostics)
    .map((revision) => ({
      id: revision.id,
      number: revision.revision,
      name: `R${revision.revision}`,

      Performance: revision.performance,

      ...Object.fromEntries(
        designFields.map((f) => [expandCamelCase(f), parseFloat(revision[f])])
      ),
      ...Object.fromEntries(
        revision.diagnostics.map((d) => [d.label, d.value])
      ),
    }))
    .toSorted((a, b) => a.number - b.number);

  const handleSetYAxisField = (e) => {
    if (e.target?.value) {
      setYAxisField(e.target.value);
    }
  };
  const yAxisFieldUnit =
    analysisFields.find((f) => f.label === yAxisField)?.unit || '';

  return (
    <>
      <div className="d-flex align-items-center justify-content-between">
        <h4 className="p-3">
          <strong>Concept Analysis:</strong> {concept.name}
        </h4>
        <Button
          size="sm"
          onClick={() => {
            selectRevision(null);
            selectConcept(null);
          }}
          variant="secondary"
          className="me-3"
        >
          Back to all concepts
        </Button>
      </div>

      <div style={{ marginLeft: '60px' }}>
        <Form>
          <Row className="mx-0 align-items-center">
            <Col xs="auto" className="px-0">
              <Form.Label column="sm" htmlFor="yAxisField">
                Y-Axis:
              </Form.Label>
            </Col>
            <Col xs="auto" className="pe-0 ps-1">
              <Form.Select
                size="sm"
                id="yAxisField"
                onChange={handleSetYAxisField.bind(this)}
              >
                {designFields.map((f) => (
                  <option value={expandCamelCase(f)} key={f}>
                    {expandCamelCase(f)}
                  </option>
                ))}
                <option disabled>---</option>
                {analysisFields.map((f) => (
                  <option value={f.label} key={f.label}>
                    {f.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </Form>
      </div>

      <div style={width ? { width } : undefined}>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart
            data={data}
            margin={{ left: 0, right: 30, top: 10, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis
              type="number"
              domain={[
                (min) => nearestDown(Math.floor(min * 95) / 100, 0.5),
                (max) => nearestUp(Math.ceil(max * 105) / 100, 0.5),
              ]}
              tickFormatter={(tick) => formatWithUnits(tick, yAxisFieldUnit)}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="linear"
              isAnimationActive={
                false
              } /* Animation is bugged for the time being - https://github.com/recharts/recharts/issues/1426 */
              stroke="#337ab7"
              dataKey={yAxisField}
              dot={<CustomizedDot selectRevision={selectRevision} />}
              activeDot={
                <CustomizedDot selectRevision={selectRevision} active />
              }
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {selectedRevision ? (
        <RevisionDetails
          key={selectedRevision.id}
          revision={selectedRevision}
          saveNotes={saveNotes}
          openRevision={openRevision}
        />
      ) : (
        <div className="d-flex justify-content-center text-muted">
          Select a design concept to view details
        </div>
      )}
    </>
  );
};

export default LogsForConcept;
